import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("pg_customer_token");
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(`${api.defaults.baseURL}/customers/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setCustomer(d))
      .catch(() => {
        localStorage.removeItem("pg_customer_token");
      })
      .finally(() => setChecking(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/customers/login", { email, password });
    localStorage.setItem("pg_customer_token", data.token);
    setCustomer({ email: data.email, name: data.name, role: "customer" });
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/customers/register", { email, password, name });
    localStorage.setItem("pg_customer_token", data.token);
    setCustomer({ email: data.email, name: data.name, role: "customer" });
    return data;
  };

  const logout = () => {
    localStorage.removeItem("pg_customer_token");
    setCustomer(null);
  };

  // Helper to fetch with customer token
  const authFetch = async (path) => {
    const token = localStorage.getItem("pg_customer_token");
    const r = await fetch(`${api.defaults.baseURL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  };

  // Memoise so customer-area components don't re-render on every parent
  // render — only when customer/checking actually change.
  const value = useMemo(
    () => ({ customer, checking, login, register, logout, authFetch }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customer, checking],
  );

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export const useCustomer = () => useContext(CustomerAuthContext);
